import React, { useEffect, useState } from "react";
import { fansApi } from "../services/fans";
import type { MyRank } from "../types/fans";
import DJLeaderboard from "../components/DJLeaderboard";
import TopDJsBoard from "../components/TopDJsBoard";
import TopFansBoard from "../components/TopFansBoard";
import LeaderboardFlame from "../components/LeaderboardFlame";
import { formatSingleAmount } from "../utils/currency";
import { ensureFanSession, getFanUser, isFanSignedIn } from "../lib/fanAccount";
import "./LeaderboardPage.css";
import "../components/FansLeaderboardSheet.css";
import "./FanLeaderboardPage.css";
import { t } from "../i18n";
import { T } from "../i18n/T";

// App fan mode — 🏆 Leaderboard tab (locked r6 §5): the DJ app's exact
// /leaderboard page (same three boards, same toggle, same explainers —
// LeaderboardPage.css does the styling) with two differences: no back
// arrow (it's a tab, not a sub-screen) and the fan's OWN rank pinned above
// the tab bar on the Top Fans view. The rank comes from GET /fans/me/rank
// (signed-in only, same money definitions as the board); no session, no
// played spend, or an older server all just mean no card — never an error.

type View = "songs" | "djs" | "fans";

const VIEW_EXPLAINERS: Record<View, string> = {
  songs: t("leaderboardPage.songsExplainer"),
  djs: t("fanLeaderboardPage.requestsFromUniqueFans"),
  fans: t("fanLeaderboardPage.biggestSpendersAcrossAll"),
};

const FanLeaderboardPage: React.FC = () => {
  const [view, setView] = useState<View>("songs");
  const [myRank, setMyRank] = useState<MyRank | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await ensureFanSession();
      if (cancelled || !isFanSignedIn()) return;
      try {
        const data = await fansApi.myRank();
        if (!cancelled && data && typeof data.rank === "number" && data.rank > 0) {
          setMyRank(data);
        }
      } catch {
        /* old server or hiccup — the board still works, just no card */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The card is a Top-Fans rank, so it rides that view only.
  const showCard = view === "fans" && !!myRank;
  const account = getFanUser();
  // The card is signed-in-only (rank needs a fan token), so the account is
  // the one identity source (founder decision).
  const initial = Array.from((account?.name || "").trim())[0]?.toUpperCase() || "🎉";

  return (
    // The scroller OWNS the scrolling (document never scrolls on this tab):
    // iOS rubber-band therefore can't drag the fixed header / tab bar /
    // rank card around — the "distorted screen" founder repro.
    <div className={`fanlb-scroll${showCard ? " fanlb-scroll--carded" : ""}`}>
    <div className="leaderboard-page">
      <div className="leaderboard-topbar">
        <h1 className="leaderboard-title">
          <T k="fanLeaderboardPage.requestAppLeaderboard" tags={{ 0: () => <LeaderboardFlame size={24} /> }} />
        </h1>
      </div>

      <div className="lb-mode-toggle" role="tablist" aria-label={t("fanLeaderboardPage.leaderboardView")}>
        <button
          type="button"
          role="tab"
          aria-selected={view === "songs"}
          className={`lb-mode-btn${view === "songs" ? " lb-mode-btn--active" : ""}`}
          onClick={() => setView("songs")}
        >
          {t("fanLeaderboardPage.songsArtists")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "djs"}
          className={`lb-mode-btn${view === "djs" ? " lb-mode-btn--active" : ""}`}
          onClick={() => setView("djs")}
        >
          {t("fanLeaderboardPage.topTalent")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "fans"}
          className={`lb-mode-btn${view === "fans" ? " lb-mode-btn--active" : ""}`}
          onClick={() => setView("fans")}
        >
          {t("fanLeaderboardPage.topFans")}
        </button>
      </div>
      <div className="lb-explainer">{VIEW_EXPLAINERS[view]}</div>

      {view === "songs" ? (
        <DJLeaderboard showHeader={false} />
      ) : view === "djs" ? (
        <TopDJsBoard />
      ) : (
        <TopFansBoard />
      )}

    </div>
      {showCard && myRank && (
        <div className="fanlb-myrank" aria-label={t("fanLeaderboardPage.yourRankNumber", { rank: myRank.rank })}>
          <span className="flb-rank fanlb-myrank-rank">#{myRank.rank}</span>
          {account?.avatarUrl ? (
            <span className="flb-photo">
              <img src={account.avatarUrl} alt="" referrerPolicy="no-referrer" />
            </span>
          ) : (
            <span className="flb-photo flb-photo--letter" aria-hidden="true">
              {initial}
            </span>
          )}
          <span className="flb-meta">
            <span className="flb-name">{t("fanLeaderboardPage.you")}</span>
            <span className="flb-sub">
              {t("fanLeaderboardPage.requestsCount", { count: myRank.requests })}
              {typeof myRank.points === "number" && myRank.points > 0
                ? t("fanLeaderboardPage.pts", { points: myRank.points.toLocaleString() })
                : ""}
            </span>
          </span>
          <span className="flb-spent">
            {formatSingleAmount(myRank.spent, myRank.currency || "usd")}
          </span>
        </div>
      )}
    </div>
  );
};

export default FanLeaderboardPage;
